import { call, put, takeLatest, all, fork } from "redux-saga/effects";
import axios from "axios";
import {
  raiseComplaintRequest,
  raiseComplaintSuccess,
  raiseComplaintFailure,
  complaintStatusRequest,
  complaintStatusSuccess,
  complaintStatusFailure,
  fetchDispositionsFailure,
  fetchDispositionsSuccess,
  fetchDispositionsRequest,
} from "./bbpsComplaintActions";
import { bbpsComplaintService } from "services/api.services/bbpsComplaint/bbpsComplaintService";
import {
  ApiEnvelope,
  RaiseComplaintResponsePayload,
  ComplaintStatusResponsePayload,
  DispositionApiPayload,
  DispositionItem,
} from "./bbpsComplaintTypes";
import { NormalizedError } from "services/axios.services/interceptors/commonInterceptors";

export function extractApiError(err: unknown): {
  message: string;
  statusCode?: number;
  errors?: any;
  httpStatus?: number;
} {
  // 1) Our NormalizedError (from interceptor)
  if (err && typeof err === "object" && "status" in err && "message" in err) {
    const ne = err as NormalizedError;
    const raw: any = (ne as any).raw;

    return {
      message: raw?.message || ne.message || "Request failed.",
      // prefer API statusCode, else HTTP status
      statusCode: raw?.statusCode ?? (ne as any).code ?? ne.status,
      httpStatus: ne.status,
      errors: raw?.errors ?? undefined,
    };
  }

  // 2) AxiosError (in case some call bypasses interceptor)
  if (axios.isAxiosError(err)) {
    const data: any = err.response?.data;
    return {
      message: data?.message || err.message || "Request failed.",
      statusCode: data?.statusCode ?? err.response?.status,
      httpStatus: err.response?.status,
      errors: data?.errors,
    };
  }

  // 3) Generic Error
  if (err && typeof err === "object" && "message" in err) {
    return {
      message: String((err as any).message || "Internal Server Error."),
    };
  }

  return { message: "Internal Server Error." };
}

function mapToItems(
  env: ApiEnvelope<DispositionApiPayload>,
): DispositionItem[] {
  const rows = env.data?.dispositions ?? [];
  return rows.map((r) => ({
    code: r.disposition,
    label:
      r.description && r.description.trim()
        ? r.description.trim()
        : r.disposition,
  }));
}

function* handleRaiseComplaint(
  action: ReturnType<typeof raiseComplaintRequest>,
) {
  try {
    const res: ApiEnvelope<RaiseComplaintResponsePayload> = yield call(
      bbpsComplaintService.raiseComplaint,
      action.payload,
    );

    if (res?.status) {
      yield put(raiseComplaintSuccess(res));
    } else {
      yield put(
        raiseComplaintFailure({
          message: res?.message || "Request failed.",
          statusCode: res?.statusCode,
          errors: res?.errors,
        }),
      );
    }
  } catch (err) {
    const e = extractApiError(err);
    yield put(
      raiseComplaintFailure({ message: e.message, statusCode: e.statusCode }),
    );
  }
}

function* handleComplaintStatus(
  action: ReturnType<typeof complaintStatusRequest>,
) {
  try {
    const res: ApiEnvelope<ComplaintStatusResponsePayload> = yield call(
      bbpsComplaintService.complaintStatus,
      action.payload,
    );

    // console.log({ res });

    if (res?.status) {
      yield put(complaintStatusSuccess(res));
    } else {
      yield put(
        complaintStatusFailure({
          message: res?.message || "Request failed.",
          statusCode: res?.statusCode,
          errors: res?.errors,
        }),
      );
    }
  } catch (err) {
    const e = extractApiError(err);
    yield put(
      complaintStatusFailure({ message: e.message, statusCode: e.statusCode }),
    );
  }
}

// function* fetchDispositions(action: PayloadAction<{ force?: boolean }>) {
function* fetchDispositions() {
  try {
    // const force = !!action.payload?.force;
    // const cached: DispositionItem[] | null = yield select(
    //   (s: RootState) => s.bbpsComplaint.dispositions.data
    // );

    // if (!force && cached && cached.length > 0) {
    //   // already loaded -> no API call
    //   return;
    // }

    const res: ApiEnvelope<DispositionApiPayload> = yield call(
      bbpsComplaintService.getDispositions,
    );

    if (!res.status) {
      yield put(
        fetchDispositionsFailure({
          message: res.message || "Failed to fetch dispositions.",
          statusCode: res.statusCode,
        }),
      );
      return;
    }

    const items = mapToItems(res);

    yield put(
      fetchDispositionsSuccess({
        items,
        message: res.message,
      }),
    );
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to fetch dispositions.";
    const code = err?.response?.data?.statusCode || err?.response?.status;

    yield put(fetchDispositionsFailure({ message: msg, statusCode: code }));
  }
}

function* watchBbpsComplaint() {
  yield takeLatest(raiseComplaintRequest.type, handleRaiseComplaint);
  yield takeLatest(complaintStatusRequest.type, handleComplaintStatus);
  yield takeLatest(fetchDispositionsRequest.type, fetchDispositions);
}

export default function* bbpsComplaintSaga() {
  yield all([fork(watchBbpsComplaint)]);
}
